import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, Modal, NumberInput, Select, TextInput } from '@mantine/core';
import type { AttendanceReportRules } from 'shared-models/browser';
import { DEFAULT_ATTENDANCE_REPORT_RULES } from 'shared-models/browser';
import { Button } from '../../../../components/Button';
import settingsClasses from './shared/settings-shared.module.scss';
import navyModal from '../../../../components/Modal/navy-modal.module.scss';

type AttendanceRulesModalProps = {
  opened: boolean;
  onClose: () => void;
  isSaving: boolean;
  rules: AttendanceReportRules;
  onSave: (next: AttendanceReportRules) => void;
};

const WEEKDAYS: Array<{ value: number; label: string }> = [
  { value: 0, label: 'ראשון' },
  { value: 1, label: 'שני' },
  { value: 2, label: 'שלישי' },
  { value: 3, label: 'רביעי' },
  { value: 4, label: 'חמישי' },
  { value: 5, label: 'שישי' },
  { value: 6, label: 'שבת' },
];

const DEFAULT_150_RANGE = {
  fromWeekday: 0,
  fromTime: '00:00',
  toWeekday: 0,
  toTime: '23:59',
} as const;

const isTime = (value: string): boolean => /^([01]\d|2[0-3]):[0-5]\d$/.test(value);

const normalizeRules = (rules?: Partial<AttendanceReportRules> | null): AttendanceReportRules => {
  const overtime125StartHours = Number.isFinite(rules?.overtime125StartHours)
    ? Math.min(24, Math.max(0, Math.floor(rules?.overtime125StartHours ?? DEFAULT_ATTENDANCE_REPORT_RULES.overtime125StartHours)))
    : DEFAULT_ATTENDANCE_REPORT_RULES.overtime125StartHours;
  const overtime150StartHours = Number.isFinite(rules?.overtime150StartHours)
    ? Math.min(24, Math.max(0, Math.floor(rules?.overtime150StartHours ?? DEFAULT_ATTENDANCE_REPORT_RULES.overtime150StartHours)))
    : DEFAULT_ATTENDANCE_REPORT_RULES.overtime150StartHours;
  const useNightShiftOvertimeRules =
    typeof rules?.useNightShiftOvertimeRules === 'boolean'
      ? rules.useNightShiftOvertimeRules
      : DEFAULT_ATTENDANCE_REPORT_RULES.useNightShiftOvertimeRules;
  const overtime150Weekdays = Array.from(
    new Set((rules?.overtime150Weekdays ?? []).filter((day) => Number.isInteger(day) && day >= 0 && day <= 6)),
  ).sort((a, b) => a - b);
  const overtime150Range = rules?.overtime150Range
    && Number.isInteger(rules.overtime150Range.fromWeekday)
    && Number.isInteger(rules.overtime150Range.toWeekday)
    && isTime(rules.overtime150Range.fromTime)
    && isTime(rules.overtime150Range.toTime)
    && rules.overtime150Range.fromWeekday >= 0
    && rules.overtime150Range.fromWeekday <= 6
    && rules.overtime150Range.toWeekday >= 0
    && rules.overtime150Range.toWeekday <= 6
    ? {
        fromWeekday: rules.overtime150Range.fromWeekday,
        fromTime: rules.overtime150Range.fromTime,
        toWeekday: rules.overtime150Range.toWeekday,
        toTime: rules.overtime150Range.toTime,
      }
    : null;
  const overtime200SpecialDates = Array.from(
    new Set((rules?.overtime200SpecialDates ?? []).filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date))),
  ).sort((a, b) => a.localeCompare(b));

  return {
    overtime125StartHours,
    overtime150StartHours,
    useNightShiftOvertimeRules,
    overtime150Range,
    overtime150Weekdays,
    overtime200SpecialDates,
  };
};

export const AttendanceRulesModal: React.FC<AttendanceRulesModalProps> = ({
  opened,
  onClose,
  isSaving,
  rules,
  onSave,
}) => {
  const [localRules, setLocalRules] = useState<AttendanceReportRules>(() => normalizeRules(rules));
  const [specialDateDraft, setSpecialDateDraft] = useState('');
  const [useRange150, setUseRange150] = useState(true);

  useEffect(() => {
    if (!opened) {
      return;
    }
    const normalized = normalizeRules(rules);
    setLocalRules(normalized);
    setUseRange150(normalized.overtime150Range !== null);
    setSpecialDateDraft('');
  }, [opened, rules]);

  const specialDates = useMemo(
    () => [...localRules.overtime200SpecialDates].sort((a, b) => a.localeCompare(b)),
    [localRules.overtime200SpecialDates],
  );

  const handleAddSpecialDate = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(specialDateDraft)) {
      return;
    }
    setLocalRules((previous) => ({
      ...previous,
      overtime200SpecialDates: Array.from(new Set([...previous.overtime200SpecialDates, specialDateDraft])).sort((a, b) => a.localeCompare(b)),
    }));
    setSpecialDateDraft('');
  };

  const handleRemoveSpecialDate = (value: string) => {
    setLocalRules((previous) => ({
      ...previous,
      overtime200SpecialDates: previous.overtime200SpecialDates.filter((date) => date !== value),
    }));
  };

  const handleSave = () => {
    if (localRules.overtime150StartHours < localRules.overtime125StartHours) {
      return;
    }
    const range = useRange150 ? (localRules.overtime150Range ?? DEFAULT_150_RANGE) : null;
    if (range && (!isTime(range.fromTime) || !isTime(range.toTime))) {
      return;
    }
    onSave(
      normalizeRules({
        ...localRules,
        overtime150Range: range,
        overtime150Weekdays: [],
      }),
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="כללי דוח נוכחות"
      size="lg"
      centered
      radius="md"
      classNames={{ root: navyModal.root }}
      closeOnClickOutside={!isSaving}
      closeOnEscape={!isSaving}
    >
      <section className={settingsClasses.section} dir="rtl">
        <p className={settingsClasses.sectionSubheading}>הגדר חישוב שעות נוספות והחרגות שכר לדוח הנוכחות.</p>

        <div className={settingsClasses.formRow}>
          <div style={{ width: '100%' }}>
            <Checkbox
              label="החשב משמרת לילה"
              checked={localRules.useNightShiftOvertimeRules}
              onChange={(event) => {
                const checked = event.currentTarget.checked;
                setLocalRules((previous) => ({ ...previous, useNightShiftOvertimeRules: checked }));
              }}
              data-testid="attendance-rules-night-shift-enabled"
            />
            <p className={settingsClasses.sectionSubheading} style={{ marginTop: 6 }}>
              במשמרת לילה שעות נוספות מחושבות שעה אחת לפני שהן מחושבות במשמרת יום.
            </p>
          </div>
        </div>

        <div className={settingsClasses.formRow}>
          <NumberInput
            label="שעות רגילות עד"
            min={0}
            max={24}
            step={1}
            value={localRules.overtime125StartHours}
            onChange={(value) => {
              const next = typeof value === 'number' ? value : DEFAULT_ATTENDANCE_REPORT_RULES.overtime125StartHours;
              setLocalRules((previous) => ({ ...previous, overtime125StartHours: Math.max(0, Math.min(24, Math.floor(next))) }));
            }}
            data-testid="attendance-rules-125-start"
          />
          <NumberInput
            label="שעות 125% עד"
            min={0}
            max={24}
            step={1}
            value={localRules.overtime150StartHours}
            onChange={(value) => {
              const next = typeof value === 'number' ? value : DEFAULT_ATTENDANCE_REPORT_RULES.overtime150StartHours;
              setLocalRules((previous) => ({ ...previous, overtime150StartHours: Math.max(0, Math.min(24, Math.floor(next))) }));
            }}
            error={
              localRules.overtime150StartHours < localRules.overtime125StartHours
                ? 'ערך זה חייב להיות גדול או שווה לשעות תחילת 125%'
                : undefined
            }
            data-testid="attendance-rules-150-start"
          />
        </div>

        <div className={settingsClasses.formRow}>
          <div style={{ width: '100%' }}>
            <div className={settingsClasses.lead}>טווח שבועי לחישוב 150%</div>
            <Checkbox
              label="הפעל טווח 150% לפי יום ושעה"
              checked={useRange150}
              onChange={(event) => {
                const checked = event.currentTarget.checked;
                setUseRange150(checked);
                setLocalRules((previous) => ({
                  ...previous,
                  overtime150Range: checked
                    ? (previous.overtime150Range ?? { ...DEFAULT_150_RANGE })
                    : null,
                }));
              }}
              data-testid="attendance-rules-150-range-enabled"
            />
            <div style={{ display: 'grid', gap: 10, marginTop: 8, opacity: useRange150 ? 1 : 0.5 }}>
              <div className={settingsClasses.formRow}>
                <Select
                  label="מיום"
                  data={WEEKDAYS.map((day) => ({ value: String(day.value), label: day.label }))}
                  value={String(localRules.overtime150Range?.fromWeekday ?? DEFAULT_150_RANGE.fromWeekday)}
                  onChange={(value) => {
                    if (!value) {
                      return;
                    }
                    setLocalRules((previous) => ({
                      ...previous,
                      overtime150Range: {
                        ...(previous.overtime150Range ?? { ...DEFAULT_150_RANGE }),
                        fromWeekday: Number(value),
                      },
                    }));
                  }}
                  disabled={!useRange150}
                  data-testid="attendance-rules-150-range-from-day"
                />
                <TextInput
                  label="בשעה"
                  type="time"
                  value={localRules.overtime150Range?.fromTime ?? DEFAULT_150_RANGE.fromTime}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setLocalRules((previous) => ({
                      ...previous,
                      overtime150Range: {
                        ...(previous.overtime150Range ?? { ...DEFAULT_150_RANGE }),
                        fromTime: value,
                      },
                    }));
                  }}
                  disabled={!useRange150}
                  data-testid="attendance-rules-150-range-from-time"
                />
              </div>
              <div className={settingsClasses.formRow}>
                <Select
                  label="עד יום"
                  data={WEEKDAYS.map((day) => ({ value: String(day.value), label: day.label }))}
                  value={String(localRules.overtime150Range?.toWeekday ?? DEFAULT_150_RANGE.toWeekday)}
                  onChange={(value) => {
                    if (!value) {
                      return;
                    }
                    setLocalRules((previous) => ({
                      ...previous,
                      overtime150Range: {
                        ...(previous.overtime150Range ?? { ...DEFAULT_150_RANGE }),
                        toWeekday: Number(value),
                      },
                    }));
                  }}
                  disabled={!useRange150}
                  data-testid="attendance-rules-150-range-to-day"
                />
                <TextInput
                  label="בשעה"
                  type="time"
                  value={localRules.overtime150Range?.toTime ?? DEFAULT_150_RANGE.toTime}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setLocalRules((previous) => ({
                      ...previous,
                      overtime150Range: {
                        ...(previous.overtime150Range ?? { ...DEFAULT_150_RANGE }),
                        toTime: value,
                      },
                    }));
                  }}
                  disabled={!useRange150}
                  data-testid="attendance-rules-150-range-to-time"
                />
              </div>
            </div>
          </div>
        </div>

        <div className={settingsClasses.formRow}>
          <div style={{ width: '100%' }}>
            <div className={settingsClasses.lead}>ימים מיוחדים ל-200%</div>
            <div className={settingsClasses.formRow}>
              <TextInput
                label="בחר תאריך"
                type="date"
                value={specialDateDraft}
                onChange={(event) => setSpecialDateDraft(event.currentTarget.value)}
                data-testid="attendance-rules-special-day-picker"
              />
              <Button variant="save" onClick={handleAddSpecialDate} disabled={!/^\d{4}-\d{2}-\d{2}$/.test(specialDateDraft)} dataTestId="attendance-rules-special-day-add">
                הוסף תאריך
              </Button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
              {specialDates.length === 0 && <span style={{ fontSize: 13, opacity: 0.7 }}>לא נבחרו תאריכים</span>}
              {specialDates.map((date) => (
                <div key={date} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 999, padding: '4px 8px' }}>
                  <span dir="ltr" style={{ fontSize: 13 }}>{date}</span>
                  <Button variant="cancel" size="sm" onClick={() => handleRemoveSpecialDate(date)} dataTestId={`attendance-rules-special-day-remove-${date}`}>
                    הסר
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={settingsClasses.formActions}>
          <Button variant="cancel" onClick={onClose} disabled={isSaving} dataTestId="attendance-rules-cancel">ביטול</Button>
          <Button
            variant="save"
            onClick={handleSave}
            loading={isSaving}
            disabled={
              localRules.overtime150StartHours < localRules.overtime125StartHours
              || (useRange150 && (!isTime(localRules.overtime150Range?.fromTime ?? '') || !isTime(localRules.overtime150Range?.toTime ?? '')))
            }
            dataTestId="attendance-rules-save"
          >
            שמור
          </Button>
        </div>
      </section>
    </Modal>
  );
};
